// CombinedReviewDetail.tsx
import React, { memo } from 'react'
import { Button, Grid } from '@mui/material'
import {
    BarChart,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    Cell,
    LineChart,
    Line,
    Legend,
} from 'recharts'
import { AccentArea, AISummary, TypewriterText } from '../../components'
import { useThemeStyling } from '../../hooks/useThemeStyling/useThemeStyling'
import './CombinedReview.scss'

interface ComplaintDataPoint {
    month: string
    US: number
    EU: number
    NA: number
    APAC: number
    LATAM: number
    total: number
}

interface AGGridDetailProps {
    data: {
        complaintData?: ComplaintDataPoint[]
        [key: string]: any
    }
}

const CombinedReviewDetail = memo(({ data }: AGGridDetailProps) => {
    const { clientTheme } = useThemeStyling()
    const complaintData = data?.complaintData || []

    // Custom tooltip formatter for star ratings
    const StarRatingsTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const { value } = payload[0]
            return (
                <div
                    style={{
                        backgroundColor: 'white',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                >
                    <p style={{ margin: 0 }}>{`${label} Ratings: ${value}`}</p>
                </div>
            )
        }
        return null
    }

    // Custom tooltip formatter for negative reviews
    const NegativeReviewsTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const { value } = payload[0]
            return (
                <div
                    style={{
                        backgroundColor: 'white',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                >
                    <p style={{ margin: 0 }}>{`${label}`}</p>
                    <p style={{ margin: 0 }}>{`Negative Reviews: ${value}`}</p>
                </div>
            )
        }
        return null
    }

    // Custom tooltip formatter for total complaints
    const TotalComplaintsTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const { value } = payload[0]
            return (
                <div
                    style={{
                        backgroundColor: 'white',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                    }}
                >
                    <p style={{ margin: 0 }}>{`${label}`}</p>
                    <p style={{ margin: 0 }}>{`Total Complaints: ${value}`}</p>
                </div>
            )
        }
        return null
    }

    // Map complaintData to LineChart's expected format
    const chartData = complaintData
        .slice() // copy
        .reverse() // so oldest is left, newest is right
        .map((d) => ({
            name: d.month,
            US: d.US,
            EU: d.EU,
            NA: d.NA,
            APAC: d.APAC,
            LATAM: d.LATAM,
        }))

    // Regional line colors using theme colors
    const regionColors = {
        US: clientTheme.charts?.pieCharts?.defaultPieChart?.[0] || '#335C92',
        EU: clientTheme.charts?.pieCharts?.defaultPieChart?.[1] || '#708c69',
        NA: clientTheme.charts?.pieCharts?.defaultPieChart?.[2] || '#acbc8f',
        APAC: clientTheme.charts?.pieCharts?.defaultPieChart?.[3] || '#bf5d50',
        LATAM: clientTheme.charts?.pieCharts?.defaultPieChart?.[4] || '#e4b667',
    }

    // Use stored star ratings data instead of generating random data
    const starRatings = data?.starRatings
    const reviewsData = starRatings
        ? [
              {
                  name: '1 Star',
                  val: starRatings.oneStar,
                  fill: '#c70602',
              },
              {
                  name: '2 Stars',
                  val: starRatings.twoStars,
                  fill: '#e26400',
              },
              {
                  name: '3 Stars',
                  val: starRatings.threeStars,
                  fill: '#ffc302',
              },
              {
                  name: '4 Stars',
                  val: starRatings.fourStars,
                  fill: '#84b65a',
              },
              {
                  name: '5 Stars',
                  val: starRatings.fiveStars,
                  fill: '#17990d',
              },
          ]
        : []

    // Use stored complaint data instead of generating random data
    const complaintsData = data?.weeklyComplaints || []
    const bbbComplaintsData = data?.bbbComplaints || []

    return (
        <div style={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
            <Grid container spacing={2}>
                {/* LineChart for Regional Complaints Trends (Last 5 Months) */}
                <Grid item xs={12}>
                    <AccentArea
                        title="Complaints Trend by Region"
                        testId="complaints-line-chart"
                        headerAction={
                            <div className="ai-summary-actions">
                                <AISummary modalSubHeaderTitle="" disabled>
                                    <TypewriterText>
                                        <p>AI is currently down</p>
                                    </TypewriterText>
                                </AISummary>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    className="emp-white-btn"
                                >
                                    Export
                                </Button>
                            </div>
                        }
                    >
                        <ResponsiveContainer height={300} width="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 20,
                                    right: 20,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <YAxis
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <Tooltip />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: '12px',
                                        color: '#535353',
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="US"
                                    stroke={regionColors.US}
                                    strokeWidth={3}
                                    dot={{ r: 2, fill: regionColors.US }}
                                    activeDot={{ r: 5, fill: regionColors.US }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="EU"
                                    stroke={regionColors.EU}
                                    strokeWidth={3}
                                    dot={{ r: 2, fill: regionColors.EU }}
                                    activeDot={{ r: 5, fill: regionColors.EU }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="NA"
                                    name="NA (excluding US)"
                                    stroke={regionColors.NA}
                                    strokeWidth={2}
                                    dot={{ r: 2, fill: regionColors.NA }}
                                    activeDot={{ r: 4, fill: regionColors.NA }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="APAC"
                                    stroke={regionColors.APAC}
                                    strokeWidth={2}
                                    dot={{ r: 2, fill: regionColors.APAC }}
                                    activeDot={{
                                        r: 4,
                                        fill: regionColors.APAC,
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="LATAM"
                                    stroke={regionColors.LATAM}
                                    strokeWidth={2}
                                    dot={{ r: 2, fill: regionColors.LATAM }}
                                    activeDot={{
                                        r: 4,
                                        fill: regionColors.LATAM,
                                    }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </AccentArea>
                </Grid>

                {/* Reviews Bar Chart */}
                <Grid item xs={12} md={4}>
                    <AccentArea
                        title="Latest Ratings"
                        testId="reviews-bar-chart"
                        headerAction={
                            <div className="ai-summary-actions">
                                <AISummary modalSubHeaderTitle="" disabled>
                                    <TypewriterText>
                                        <p>AI is currently down</p>
                                    </TypewriterText>
                                </AISummary>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    className="emp-white-btn"
                                >
                                    Export
                                </Button>
                            </div>
                        }
                    >
                        <ResponsiveContainer height={220} width="100%">
                            <BarChart
                                layout="vertical"
                                data={reviewsData}
                                margin={{
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                    bottom: 0,
                                }}
                                barCategoryGap="20%"
                            >
                                <XAxis
                                    type="number"
                                    domain={[0, 'dataMax']}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={65}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <Tooltip content={<StarRatingsTooltip />} />
                                <Bar dataKey="val" maxBarSize={30}>
                                    {reviewsData.map((entry) => (
                                        <Cell
                                            key={`cell-${entry.name}`}
                                            fill={entry.fill}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </AccentArea>
                </Grid>

                {/* BBB Complaints Bar Chart */}
                <Grid item xs={12} md={4}>
                    <AccentArea
                        title="Negative Reviews"
                        testId="bbb-complaints-bar-chart"
                        headerAction={
                            <div className="ai-summary-actions">
                                <AISummary modalSubHeaderTitle="" disabled>
                                    <TypewriterText>
                                        <p>AI is currently down</p>
                                    </TypewriterText>
                                </AISummary>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    className="emp-white-btn"
                                >
                                    Export
                                </Button>
                            </div>
                        }
                    >
                        <ResponsiveContainer height={220} width="100%">
                            <BarChart
                                data={bbbComplaintsData}
                                margin={{
                                    top: 5,
                                    right: 5,
                                    left: -20,
                                    bottom: 5,
                                }}
                            >
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <YAxis
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <Tooltip content={<NegativeReviewsTooltip />} />
                                <Bar
                                    dataKey="val"
                                    fill={clientTheme.charts.fill}
                                    maxBarSize={40}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </AccentArea>
                </Grid>

                {/* <Grid item xs={12} md={4}>
                     <AccentArea
                         title="MID Health"
                         testId="mid-health-chart"
                     >
                         <div className="combined-review-mid-health">
                             <div className="combined-review-mid-health-risk-rating">
                                 <div className="risk-rating-container">
                                     <span className="risk-rating-label">Risk Rating</span>
                                     <RiskPill value={midHealthData.riskRating} />
                                 </div>
                                 <div className="risk-score-section">
                                     <div className="risk-score-label">Risk Score:</div>
                                     <div className="risk-score-content">
                                         <span className="risk-score-value">
                                             {midHealthData.riskScore}
                                         </span>
                                         <span className={`risk-score-change ${midHealthData.riskScoreChange >= 0 ? 'positive' : 'negative'}`}>
                                             ({midHealthData.riskScoreChange >= 0 ? '+' : ''}{midHealthData.riskScoreChange}% from last period)
                                         </span>
                                     </div>
                                 </div>
                             </div>

                   
                             <div className="combined-review-mid-health-metrics">
                                 <div className="combined-review-mid-health-item">
                                     <div className="metric-label">
                                         Transactions
                                     </div>
                                     <div className="metric-value">
                                         {midHealthData.transactions.toLocaleString()}
                                     </div>
                                     <div className={`metric-change ${midHealthData.transactionsChange >= 0 ? 'transactions-positive' : 'transactions-negative'}`}>
                                         {midHealthData.transactionsChange >= 0 ? '+' : ''}{midHealthData.transactionsChange}% from last period
                                     </div>
                                 </div>

                                 <div className="combined-review-mid-health-item">
                                     <div className="metric-label">
                                         Chargebacks
                                     </div>
                                     <div className="metric-value">
                                         {midHealthData.chargebacks}
                                     </div>
                                     <div className={`metric-change ${midHealthData.chargebacksChange >= 0 ? 'chargebacks-positive' : 'chargebacks-negative'}`}>
                                         {midHealthData.chargebacksChange >= 0 ? '+' : ''}{midHealthData.chargebacksChange}% from last period
                                     </div>
                                 </div>

                                 <div className="combined-review-mid-health-item">
                                     <div className="metric-label">
                                         Pre-CB
                                     </div>
                                     <div className="metric-value">
                                         {midHealthData.preCb}
                                     </div>
                                     <div className={`metric-change ${midHealthData.preCbChange >= 0 ? 'precb-positive' : 'precb-negative'}`}>
                                         {midHealthData.preCbChange >= 0 ? '+' : ''}{midHealthData.preCbChange}% from last period
                                     </div>
                                 </div>

                                 <div className="combined-review-mid-health-item">
                                     <div className="metric-label">
                                         Pre-CB/Trans Ratio
                                     </div>
                                     <div className="metric-value">
                                         {midHealthData.preCbRatio}
                                     </div>
                                 </div>
                             </div>
                         </div>
                     </AccentArea>
                 </Grid> */}

                {/* Complaints Bar Chart */}
                <Grid item xs={12} md={4}>
                    <AccentArea
                        title="Total Complaints"
                        testId="total-complaints-bar-chart"
                        headerAction={
                            <div className="ai-summary-actions">
                                <AISummary modalSubHeaderTitle="" disabled>
                                    <TypewriterText>
                                        <p>AI is currently down</p>
                                    </TypewriterText>
                                </AISummary>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    className="emp-white-btn"
                                >
                                    Export
                                </Button>
                            </div>
                        }
                    >
                        <ResponsiveContainer height={220} width="100%">
                            <BarChart
                                data={complaintsData}
                                margin={{
                                    top: 5,
                                    right: 5,
                                    left: -20,
                                    bottom: 5,
                                }}
                            >
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <YAxis
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                <Tooltip content={<TotalComplaintsTooltip />} />
                                <Bar
                                    dataKey="val"
                                    fill={clientTheme.primary}
                                    maxBarSize={40}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </AccentArea>
                </Grid>
            </Grid>
        </div>
    )
})

export default CombinedReviewDetail
